import { Controller } from "@hotwired/stimulus"

const minute = 60 * 1000
const hour = 60 * minute
const day = 24 * hour
const week = 7 * day
// Months/years are approximate durations for display, not calendar arithmetic.
const year = 365.25 * day
const month = year / 12

export function relativeAge(timestamp, now = Date.now()) {
    const elapsed = Math.max(0, now - timestamp)
    if (!Number.isFinite(elapsed)) return ""
    if (elapsed < minute) return "less than a minute ago"
    let unit, duration
    if (elapsed < hour) { unit = "minute"; duration = minute }
    else if (elapsed < day) { unit = "hour"; duration = hour }
    else if (elapsed < week) { unit = "day"; duration = day }
    else if (elapsed < 4 * week) { unit = "week"; duration = week }
    else if (elapsed < 12 * month) { unit = "month"; duration = month }
    else { unit = "year"; duration = year }
    const count = Math.max(1, Math.floor(elapsed / duration))
    return `${count} ${unit}${count === 1 ? "" : "s"} ago`
}

// One timer per server-rendered frame, including historical threads where the
// history poll is paused. Newly replaced Turbo frames connect normally.
export class RelativeTimeController extends Controller {
    static targets = ["timestamp", "day"]

    connect() {
        this.update()
        this.timer = setInterval(() => this.update(), 30000)
        this.visibilityChanged = () => { if (!document.hidden) this.update() }
        document.addEventListener("visibilitychange", this.visibilityChanged)
    }

    disconnect() {
        clearInterval(this.timer)
        document.removeEventListener("visibilitychange", this.visibilityChanged)
    }

    timestampTargetConnected(element) { this.updateTimestamp(element) }
    update() {
        for (const element of this.timestampTargets) this.updateTimestamp(element)
        let previous
        for (const element of this.dayTargets || []) {
            const date = new Date(element.dataset.date)
            if (!Number.isFinite(date.getTime())) continue
            const day = date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
            element.hidden = day === previous
            element.textContent = day
            previous = day
        }
    }

    showAbsolute(event) { this.render(event.currentTarget, true) }
    showRelative(event) { this.updateTimestamp(event.currentTarget) }

    updateTimestamp(element) { this.render(element, element.matches(":hover, :focus")) }

    render(element, absolute) {
        const date = new Date(element.dateTime)
        if (!Number.isFinite(date.getTime())) return
        if (absolute) {
            const calendar = date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
            const clock = date.toLocaleTimeString("en-US", {
                hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short"
            })
            element.textContent = `${calendar} at ${clock}`
        } else if (element.dataset?.timeFormat === "clock") {
            element.textContent = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
        } else if (element.dataset?.timeFormat === "short-date") {
            element.textContent = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        } else element.textContent = relativeAge(date.getTime())
    }
}
